import React, { useCallback, useEffect, useRef, useState } from "react";
import PageDescription from "../../DataAnalyst/sections/PageDescription";
import FileUpload from "./sections/FileUpload";
import MastersLibrary from "./sections/MastersLibrary";
import PreviewSection from "./sections/PreviewSection";
import { useDispatch, useSelector } from "react-redux";
import ProcessingLoader from "./sections/ProcessingLoader";
import * as actions from "../../../../redux/actions/audioEngineer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChatInput from "../../AmplitudeAI/Chat/ChatInput";
import { useLocation, useNavigate } from "react-router-dom";
import { sendMessage } from "../../../../redux/actions";
import { showToast } from "../../../../helper/ShowToast";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";
import { CreditPlanModal } from "../../../../components/CreditPlanModal";
import Settings from "./sections/Settings";
import AppTour from "../../../../components/AppTour/AppTour";

const steps = [
  {
    target: "[data-tour='upload-section']",
    content: "Upload your track - Start by uploading your mix stems or full mix. Our AI can work with a variety of file types and will analyze your audio to prepare it for mastering.",
    placement: "top",
    title: "Upload Your Track"
  },
  {
    target: "[data-tour='amplitude-ai-chat']",
    content: "Amplitude AI Chat - Get personalized mastering recommendations, release strategies. Just ask Amplitude AI for insights and guidance to make the most of your music.",
    placement: "top",
    title: "Amplitude AI Chat"
  },
  {
    target: "[data-tour='masters-library']",
    content: "Masters Library - Store, Share, Download and manage your mastered tracks, keep everything organized and easily accessible for release.",
    placement: "top",
    title: "Masters Library"
  }
];

export default function AudioEngineer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const pageTab = params.get("tab");
  const [runTour, setRunTour] = useState(false);
  const [newFile, setNewFile] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
  const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // NEW: Track if settings should be shown

  const localGenre = localStorage.getItem('genre');
  const localLoudness = localStorage.getItem('loudness');
  const localSampleRate = localStorage.getItem('sampleRate');
  const [fileSampleRates, setFileSampleRates] = useState([]);
  const [genre, setGenre] = React.useState(localGenre || "ROCK_INDIE");
  const [loudness, setLoudness] = React.useState(localLoudness || "MEDIUM");
  const [sampleRate, setSampleRate] = React.useState(localSampleRate || "48000");

  const { preview, taskMeta, uploadedData } = useSelector((state) => state.audioEngineer);
  const { showLoad } = useSelector((state) => state?.amplitudeReducer);
  const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
  const { taskIds } = taskMeta || {};
  const allSuccess =
    (Array.isArray(preview) && preview.length > 0
      ? preview.every(item => item?.success === true)
      : typeof preview === 'object'
        ? !!(preview?.success ?? preview?.data?.success)
        : false);

  const allMastered =
    (() => {
      if (!preview) return false;
      if (Array.isArray(preview) && preview.length > 0) return preview.every(item => item?.isMastered === true);
      if (Array.isArray(preview?.data) && preview.data.length > 0) return preview.data.every(item => item?.isMastered === true);
      if (typeof preview === 'object') return !!(preview?.isMastered ?? preview?.data?.isMastered);
      return false;
    })();

  const handleFileSelect = useCallback(async (file) => {
    setNewFile(false);
    setShowSettings(true);
    setShowTransition(false);

    const fileArray = Array.isArray(file)
      ? file
      : (file && file.length ? Array.from(file) : [file]);

    const validFiles = fileArray.filter(f => f && f instanceof File);

    if (validFiles.length === 0) {
      showToast("error", "No valid files selected");
      return;
    }

    const extractedSampleRates = [];

    // Create ONE AudioContext (not per file)
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    for (const f of validFiles) {
      if (!f.type.startsWith("audio/")) {
        extractedSampleRates.push(null);
        continue;
      }

      try {
        const arrayBuffer = await f.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        extractedSampleRates.push(audioBuffer.sampleRate);
      } catch (error) {
        console.error(`Failed to extract sample rate from ${f.name}`, error);
        extractedSampleRates.push(null);
      }
    }

    audioContext.close();

    const finalSampleRate =
      extractedSampleRates.find(rate => rate !== null) || sampleRate;

    localStorage.setItem("genre", genre);
    localStorage.setItem("loudness", loudness);
    localStorage.setItem("sampleRate", String(finalSampleRate));

    setFileSampleRates(extractedSampleRates);

    dispatch(
      actions.uploadAndGetPreview(
        toast,
        validFiles,
        setIsUploading,
        "mastering",
        false,
        "",
        () => { }
      )
    );

  }, [dispatch, genre, loudness, sampleRate]);


  const handleSettingsForPreview = useCallback(async () => {
    const fileUrls = uploadedData?.data?.map(item => item.readable_url);
    const uploadIds = uploadedData?.data?.map(item => item._id);

    // Update localStorage with current settings
    localStorage.setItem('genre', genre);
    localStorage.setItem('loudness', loudness);
    localStorage.setItem('sampleRate', sampleRate);

    let options = {
      musicalStyle: genre,
      desiredLoudness: loudness,
      sampleRates: ["48000"], // Send array of sample rates
    };

    const payload = {
      fileUrls,
      uploadIds,
      options,
    };

    // Validate required fields before processing
    const missing = [];
    if (!fileUrls || !Array.isArray(fileUrls) || fileUrls.length === 0) missing.push('fileUrls');
    if (!uploadIds || !Array.isArray(uploadIds) || uploadIds.length === 0) missing.push('uploadIds');
    if (!options.musicalStyle) missing.push('genre');
    if (!options.desiredLoudness) missing.push('loudness');
    if (!options.sampleRates || !Array.isArray(options.sampleRates) || options.sampleRates.length === 0 || options.sampleRates.some(x => !x)) missing.push('sampleRate');

    if (missing.length > 0) {
      const friendly = missing.map((m) => {
        switch (m) {
          case 'fileUrls': return 'uploaded files';
          case 'uploadIds': return 'upload IDs';
          case 'genre': return 'genre';
          case 'loudness': return 'loudness';
          case 'sampleRate': return 'sample rate(s)';
          default: return m;
        }
      });
      showToast('error', `Please provide/select the following before proceeding: ${friendly.join(', ')}`);
      // keep settings visible so user can correct
      setShowSettings(true);
      return;
    }

    // Hide settings and prepare for processing
    setShowSettings(false);

    // Create the mastering preview
    await dispatch(actions.createMasteringPreview(toast, payload, setIsUploading));
  }, [dispatch, uploadedData, genre, loudness, sampleRate, fileSampleRates]);

  const handleNew = React.useCallback(() => {
    // mark UI state
    navigate('/admin/urstudio/audio-engineer')
    setNewFile(true);
    setShowSettings(false);
    setShowTransition(false);

    // attempt to clear redux preview state
    // dispatch(actions.previewDataStatus({ isMaster: false }));
    dispatch(actions.previewData({}));

    // detect if there's any existing data we should wipe / reload for
    const hasPreviewData = Boolean(
      preview ||
      (uploadedData && Array.isArray(uploadedData.data) && uploadedData.data.length > 0) ||
      (taskIds && Array.isArray(taskIds) && taskIds.length > 0)
    );

    if (hasPreviewData) {
      setTimeout(() => {
        window.location.reload();
      }, 100);
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [dispatch, preview, uploadedData, taskIds]);

  const getSettings = (e) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSend = (input) => {
    if (!hasActiveSubscription) {
      showToast("info", "Please subscribe to continue using Amplitude AI.");
      setShowPricingPlanModal(true);
      return;
    }

    if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
      showToast("info", "You are out of credits. Please purchase more to continue.");
      setShowCreditPlanModal(true);
      return;
    }

    dispatch(sendMessage(toast, input));
    navigate("/admin/amplitude-ai");
  };

  useEffect(() => {
    if (pageTab === "settings") {
      setNewFile(false)
      setShowSettings(true)
      setFileSampleRates([localSampleRate || sampleRate]) // Initialize file sample rates with user setting or default
    }
    if (pageTab === "preview") {
      setNewFile(false)
    }
  }, [pageTab]);

  useEffect(() => {
    if (location.hash === "#start-tour") {
      setRunTour(true);
    }
  }, [location.hash])

  const onExit = () => {
    dispatch(actions.clearUploadData());
    dispatch(actions.uploadData({}))
    dispatch(actions.previewData({}))
    dispatch(actions.taskMetaData({}))
    navigate('/admin/urstudio')
  }

  return (
    <div className="DataAnalyst-page py-5">
      <ToastContainer />
      <AppTour
        tourSteps={steps}
        runTour={runTour}
        setRunTour={setRunTour}
      />
      <div className="container max-w-full">
        <PageDescription
          title="Your Mastering Engineer"
          description="Create studio-quality masters in minutes — powered by intelligent, genre-aware AI."
        />
        <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
          {newFile && !allMastered && (
            <FileUpload onFileSelect={handleFileSelect} />
          )}

          {/* NEW: Show settings after upload */}
          {showSettings && uploadedData?.data?.[0]?.operation === 'mastering' && (
            <div className="bg-white flex flex-col items-center gap-4 relative max-w-[500px] mx-auto rounded-2xl p-4 lg:p-5">
              <Settings
                genre={genre}
                setGenre={setGenre}
                loudness={loudness}
                setLoudness={setLoudness}
                sampleRate={sampleRate}
                setSampleRate={setSampleRate}
              />
              {uploadedData?.data?.length > 0 && (
                <>
                  <div className="w-full mt-4 space-y-3">
                    {uploadedData.data.map((file, index) => {

                      let progress = 30;
                      let label = "30%";

                      if (file.roexStatus === "LOCAL_UPLOADING") {
                        progress = 15;
                        label = "15%";
                      }

                      if (file.roexStatus === "UPLOADING_TO_ROEX") {
                        progress = 60;
                        label = "60%";
                      }

                      if (file.roexStatus === "UPLOADED_TO_ROEX") {
                        progress = 100;
                        label = "100%";
                      }

                      if (file.roexStatus === "FAILED") {
                        progress = 100;
                        label = "Failed";
                      }

                      return (
                        <div key={index} className="w-full">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="truncate max-w-[70%]">
                              {file.filename}
                            </span>
                            <span>{label}</span>
                          </div>

                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-700 ${file.roexStatus === "FAILED"
                                ? "bg-red-500"
                                : "bg-[#FA6138]"
                                }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleSettingsForPreview}
                    className={`mt-2 w-full max-w-md bg-[#FA6138] text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium ${isUploading ? 'opacity-70' : 'opacity-100'}`}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Processing...' : 'Save Settings & Start Processing'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Show processing loader */}
          {isUploading && !showSettings && (
            <ProcessingLoader isMaster={false} uploadId={uploadedData?.data?.[0]?._id || null} taskId={taskIds?.[0] || null} />
          )}

          {/* Show preview when ready (either allSuccess OR allMastered) */}
          {!(newFile && !allMastered) && (!isUploading || pageTab === "preview") && (allSuccess || allMastered) && !showSettings && (
            <>
              {allMastered && (
                <div className="max-w-[700px] mx-auto">
                  <div className="bg-white mb-4 rounded-xl px-4 py-2 w-full">
                    <button onClick={onExit} className="bg-theme_clr2 bg-opacity-50 text-theme_clr4 font-medium text-sm rounded-md px-4 py-2"><i className="bi bi-arrow-left-square mr-1"></i> Exit</button>
                  </div>
                </div>
              )}
              <PreviewSection taskIds={taskIds} />
            </>
          )}
        </div>

        <div className="mt-4" data-tour="amplitude-ai-chat">
          <ChatInput
            title={'Plan your next move with your mastered tracks.'}
            inputPlaceholder={"Let Amplitude AI design the perfect release strategy."}
            onSend={handleSend}
            isDisabled={showLoad}
          />
        </div>

        <div className="mt-4" data-tour="masters-library">
          <MastersLibrary addNew={handleNew} handleSettings={getSettings} />
        </div>
      </div>

      <PricingPlanModal
        isOpen={showPricingPlanModal}
        onClose={() => setShowPricingPlanModal(false)}
        returnUrl={window.location.origin + "/admin/audio-engineer"}
      />
      <CreditPlanModal
        isOpen={showCreditPlanModal}
        onClose={() => setShowCreditPlanModal(false)}
      />
    </div>
  );
}
